/**
 * Obras que morreram depois de as partes se conhecerem.
 *
 * Este ecrã não acusa ninguém. Uma obra abandonada pode ser um cliente que
 * ficou sem dinheiro, que mudou de ideias, ou que foi combinar por fora — e do
 * lado de fora não se distingue. O que se distingue é o **par que se repete**:
 * o mesmo cliente a deixar morrer duas obras com a mesma empresa deixa de ser
 * coincidência, e é por isso que essa coluna está aqui.
 *
 * A acção é telefonar. Por isso os dois números estão clicáveis, lado a lado —
 * quem liga precisa de ouvir as duas versões.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertTriangle, Phone, Repeat } from 'lucide-react';
import api from '../../utils/api';

const kz = (v) => `${Number(v || 0).toLocaleString('pt-PT')} Kz`;
const dt = (v) => (v ? new Date(v).toLocaleDateString('pt-PT', { timeZone: 'Africa/Luanda' }) : '—');

const FugasPage = () => {
  const [dados, setDados] = useState(null);

  const carregar = useCallback(() => {
    api.get('/projects/fugas/suspeitas')
      .then((r) => setDados(r.data))
      .catch(() => setDados({ obras: [], dias: 10 }));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  if (!dados) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-600" /></div>;
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-amber-400" /> Obras paradas depois do contrato
        </h1>
        <p className="text-sm text-slate-400 mt-1 max-w-2xl">
          Contratos criados e nunca pagos, parados há mais de {dados.dias} dias. Nesta
          altura o cliente já sabe quem é a empresa — é o momento em que uma obra pode
          sair da plataforma. Não é prova de nada: é uma lista para telefonar.
        </p>
      </div>

      {dados.obras.length === 0 ? (
        <div className="p-6 rounded-xl border border-slate-800 bg-slate-900 text-center text-slate-400 text-sm">
          Nenhuma obra parada. Todos os contratos recentes foram pagos ou ainda estão dentro do prazo.
        </div>
      ) : (
        <ul className="space-y-2">
          {dados.obras.map((o) => (
            <li key={o.project_id} className="p-4 rounded-xl border border-slate-800 bg-slate-900 space-y-2">
              <div className="flex gap-3 items-start flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <div className="font-semibold text-slate-100">{o.titulo || o.categoria}</div>
                  <div className="text-xs text-slate-500 font-mono">
                    {o.codigo} · {o.contrato} · {kz(o.valor_actual)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-amber-400 font-semibold">{o.dias_parada} dias parada</div>
                  <div className="text-xs text-slate-500">desde {dt(o.parada_desde)}</div>
                </div>
              </div>

              {/* O que já tinha acontecido quando parou. Um contrato assinado
                  pesa mais do que um por assinar: significa que as duas partes
                  chegaram a acordo e só faltou o dinheiro entrar. */}
              <div className="flex gap-2 flex-wrap text-[11px]">
                {/* Pagou, viu quem era, e pediu o dinheiro de volta. Não é a
                    mesma coisa que uma obra que apodreceu por pagar: aqui o
                    cliente teve o nome, o NIF e o endereco da empresa à frente
                    e decidiu sair. É o caso que esta lista existe para apanhar. */}
                {o.tipo === 'PAGOU_E_SAIU' && (
                  <span className="px-2 py-0.5 rounded bg-red-500/20 text-red-300 font-medium">
                    pagou, viu a empresa e desistiu
                  </span>
                )}
                <span className={`px-2 py-0.5 rounded ${o.assinado
                  ? 'bg-amber-500/15 text-amber-400' : 'bg-slate-800 text-slate-400'}`}>
                  {o.assinado ? 'assinado pelos dois' : 'por assinar'}
                </span>
                {o.prazo_passou && (
                  <span className="px-2 py-0.5 rounded bg-red-500/15 text-red-400">
                    prazo de pagamento ultrapassado
                  </span>
                )}
                {o.repeticoes > 1 && (
                  <span className="px-2 py-0.5 rounded bg-red-500/20 text-red-300 inline-flex items-center gap-1">
                    <Repeat className="w-3 h-3" /> {o.repeticoes}.ª obra deste par
                  </span>
                )}
              </div>

              <div className="grid gap-2 sm:grid-cols-2 pt-2 border-t border-slate-800">
                {[[o.cliente, o.cliente_telefone, 'Cliente'],
                  [o.empresa, o.empresa_telefone, 'Empresa']].map(([nome, tel, papel]) => (
                  <div key={papel} className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-slate-500 uppercase tracking-wide">{papel}</div>
                      <div className="text-sm text-slate-200 truncate">{nome || '—'}</div>
                    </div>
                    {tel && (
                      <a href={`tel:${tel}`}
                        className="min-h-[40px] px-3 rounded-lg bg-slate-800 text-slate-200 text-xs inline-flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5" /> Ligar
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FugasPage;
